import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { Ticket, Gift, Sparkles, Trophy, ArrowRight, Users, Clock } from 'lucide-react';
import { useLang } from '@/contexts/LanguageContext';
import JellyLogo from '@/components/JellyLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// How long (ms) to keep the winner banner visible after the draw before
// switching back to the participant counter view.
const WINNER_DISPLAY_MS = 30000;

export default function RaffleLive() {
  const { lang } = useLang();
  const [data, setData] = useState({
    total_participants: 0,
    winners_count: 0,
    recent: [],
    winners: [],
    server_time: null,
    scheduled_at: null,
    schedule_drawn: false,
    schedule_drawn_at: null,
    latest_winners: [],
  });
  const [now, setNow] = useState(() => Date.now());
  const [showWinner, setShowWinner] = useState(false);
  const winnerSeenRef = useRef(false);
  const hideTimerRef = useRef(null);
  const confettiTimerRef = useRef(null);

  // Poll data every 3s
  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/raffle/live`);
      setData(res.data);
    } catch (err) {
      // Poll endpoint — log for diagnostics but don't spam the user with toasts.
      console.error('[RaffleLive] poll failed:', err?.message || err);
    }
  };

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 3000);
    return () => clearInterval(id);
  }, []);

  // Local clock tick (for countdown)
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Compute server-clock offset so the countdown is not affected by client clock drift.
  const offsetRef = useRef(0);
  useEffect(() => {
    if (data.server_time) {
      const serverMs = new Date(data.server_time).getTime();
      offsetRef.current = serverMs - Date.now();
    }
  }, [data.server_time]);

  // Detect when a scheduled draw just happened: fire confetti + show winner banner
  useEffect(() => {
    const drawnAt = data.schedule_drawn_at ? new Date(data.schedule_drawn_at).getTime() : 0;
    const hasWinners = data.latest_winners && data.latest_winners.length > 0;
    const recent = drawnAt && (Date.now() + offsetRef.current - drawnAt) < WINNER_DISPLAY_MS;

    if (hasWinners && recent && !winnerSeenRef.current) {
      winnerSeenRef.current = true;
      setShowWinner(true);
      fireConfetti();
      // Hide after the display window expires
      const remaining = Math.max(2000, WINNER_DISPLAY_MS - (Date.now() + offsetRef.current - drawnAt));
      hideTimerRef.current = setTimeout(() => setShowWinner(false), remaining);
    }

    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [data.schedule_drawn_at, data.latest_winners.length]);

  const fireConfetti = () => {
    const duration = 6000;
    const end = Date.now() + duration;
    const colors = ['#FF7597', '#F4DD8C', '#A99BE0', '#FFFFFF'];
    (function frame() {
      confetti({
        particleCount: 4,
        angle: 60,
        spread: 70,
        origin: { x: 0, y: 0.6 },
        colors,
      });
      confetti({
        particleCount: 4,
        angle: 120,
        spread: 70,
        origin: { x: 1, y: 0.6 },
        colors,
      });
      if (Date.now() < end) confettiTimerRef.current = requestAnimationFrame(frame);
    })();
  };

  useEffect(() => () => {
    if (confettiTimerRef.current) cancelAnimationFrame(confettiTimerRef.current);
  }, []);

  // ---------- Derived values ----------
  const scheduledMs = data.scheduled_at ? new Date(data.scheduled_at).getTime() : 0;
  const nowSrv = now + offsetRef.current;
  const remainingMs = Math.max(0, scheduledMs - nowSrv);
  const isFuture = scheduledMs > 0 && remainingMs > 0;
  const isPastNotDrawn = scheduledMs > 0 && remainingMs === 0 && !data.schedule_drawn;

  return (
    <div className="min-h-screen bg-[#A99BE0] text-white overflow-hidden relative">
      <div className="absolute -top-32 -left-32 w-[40rem] h-[40rem] rounded-full bg-[#F4DD8C]/40 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-[40rem] h-[40rem] rounded-full bg-[#FF7597]/40 blur-3xl pointer-events-none" />
      <div className="absolute inset-0 grain pointer-events-none opacity-50" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-16">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <Link to="/" className="inline-block">
            <JellyLogo size="lg" color="#FF2D9C" bylineColor="#FF2D9C" />
          </Link>
          <span className="inline-flex items-center gap-2 self-start text-[11px] md:text-xs font-bold tracking-[0.3em] uppercase text-[#1F1A24] bg-[#F4DD8C] px-4 py-2 rounded-full">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF5C83] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#FF5C83]"></span>
            </span>
            EN VIVO · EXPOBELLEZA 2026
          </span>
        </div>

        {/* WINNER REVEAL (priority view) */}
        {showWinner ? (
          <WinnerReveal winners={data.latest_winners} lang={lang} />
        ) : isFuture ? (
          <CountdownView
            remainingMs={remainingMs}
            scheduledAt={data.scheduled_at}
            total={data.total_participants}
            lang={lang}
          />
        ) : isPastNotDrawn ? (
          <DrawingView lang={lang} />
        ) : (
          <CounterView data={data} lang={lang} />
        )}

        {/* Recent participants — hide during winner reveal */}
        {!showWinner && (
          <div className="mt-16">
            <p className="text-[10px] md:text-xs font-bold tracking-[0.3em] uppercase text-white/70 mb-6">
              {lang === 'es' ? 'ÚLTIMOS EN INSCRIBIRSE' : 'JUST JOINED'}
            </p>
            {data.recent.length === 0 ? (
              <p className="text-white/60 italic">
                {lang === 'es' ? 'Sé el primero en inscribirte 💕' : 'Be the first to join 💕'}
              </p>
            ) : (
              <div className="flex flex-wrap gap-3">
                {data.recent.map((r, i) => (
                  <div
                    key={`${r.first_name}-${r.created_at}-${i}`}
                    className="inline-flex items-center gap-2 rounded-full bg-white/15 border border-white/25 backdrop-blur px-4 py-2 text-sm animate-fade-up"
                    style={{ animationDelay: `${i * 80}ms` }}
                  >
                    <span className="h-2 w-2 rounded-full bg-[#F4DD8C]" />
                    <span className="font-bold capitalize">{r.first_name || '—'}</span>
                    {r.city && <span className="text-white/70 text-xs">· {r.city}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <p className="mt-20 text-center text-[10px] tracking-[0.3em] uppercase text-white/40">
          {lang === 'es' ? 'ACTUALIZA CADA 3S · ' : 'UPDATES EVERY 3S · '} jellyupmamacita.com/sorteo
        </p>
      </div>
    </div>
  );
}

// ---------- Subviews ----------

const CounterView = ({ data, lang }) => (
  <>
    <div className="mt-12 md:mt-20 text-center">
      <p className="text-[10px] md:text-xs font-bold tracking-[0.4em] uppercase text-white/70">
        {lang === 'es' ? 'PARTICIPANTES INSCRITOS' : 'REGISTERED PARTICIPANTS'}
      </p>
      <div
        data-testid="raffle-counter"
        className="mt-6 font-brand text-[28vw] md:text-[18vw] lg:text-[16rem] leading-[0.85] text-[#F4DD8C]"
        style={{ textShadow: '6px 6px 0 rgba(31,26,36,0.15)' }}
      >
        <AnimatedNumber value={data.total_participants} />
      </div>
      <p className="mt-6 max-w-2xl mx-auto text-white text-base md:text-xl">
        {lang === 'es'
          ? 'Cada registro suma un boleto al sorteo de kits Jelly. ¡Únete tú también!'
          : 'Every registration adds a ticket to the Jelly kits raffle. Join now!'}
      </p>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Link
          to="/registro"
          className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#FF7597] text-white text-sm font-bold tracking-widest uppercase hover:bg-[#FF5C83] hover:scale-105 transition-all shadow-[0_12px_40px_rgba(255,117,151,0.35)]"
        >
          <Gift className="w-4 h-4" />
          {lang === 'es' ? 'Quiero participar' : 'I want to enter'}
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
    <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-4xl mx-auto">
      <StatPill icon={<Users className="w-5 h-5" />} label={lang === 'es' ? 'Inscritos' : 'Entries'} value={data.total_participants} />
      <StatPill icon={<Ticket className="w-5 h-5" />} label={lang === 'es' ? 'Boletos activos' : 'Active tickets'} value={Math.max(0, data.total_participants - data.winners_count)} />
      <StatPill icon={<Trophy className="w-5 h-5" />} label={lang === 'es' ? 'Ganadores' : 'Winners'} value={data.winners_count} accent />
      <StatPill icon={<Sparkles className="w-5 h-5" />} label={lang === 'es' ? 'Kits disponibles' : 'Kits available'} value={Math.max(0, 4 - data.winners_count)} />
    </div>
  </>
);

const CountdownView = ({ remainingMs, scheduledAt, total, lang }) => {
  const s = Math.floor(remainingMs / 1000);
  const days = Math.floor(s / 86400);
  const hrs = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;

  const sched = scheduledAt ? new Date(scheduledAt) : null;
  const schedLabel = sched
    ? sched.toLocaleString(lang === 'es' ? 'es-CO' : 'en-US', {
        weekday: 'short', day: '2-digit', month: 'short',
        hour: '2-digit', minute: '2-digit', hour12: true,
        timeZone: 'America/Bogota',
      })
    : '';

  return (
    <div data-testid="raffle-countdown" className="mt-10 md:mt-16 text-center">
      <p className="inline-flex items-center gap-2 text-[10px] md:text-xs font-bold tracking-[0.4em] uppercase text-[#1F1A24] bg-[#F4DD8C] px-4 py-2 rounded-full">
        <Clock className="w-3.5 h-3.5" />
        {lang === 'es' ? 'SORTEO PROGRAMADO' : 'RAFFLE SCHEDULED'}
      </p>
      <h2 className="mt-6 font-display text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter text-white text-balance">
        {lang === 'es' ? 'El sorteo arranca en…' : 'The raffle starts in…'}
      </h2>

      <div className="mt-10 grid grid-cols-4 gap-3 md:gap-6 max-w-5xl mx-auto">
        <CountdownCell value={days} label={lang === 'es' ? 'Días' : 'Days'} />
        <CountdownCell value={hrs} label={lang === 'es' ? 'Horas' : 'Hours'} />
        <CountdownCell value={mins} label={lang === 'es' ? 'Min' : 'Min'} />
        <CountdownCell value={secs} label={lang === 'es' ? 'Seg' : 'Sec'} pulse />
      </div>

      {schedLabel && (
        <p className="mt-8 text-white/80 text-sm md:text-base">
          {lang === 'es' ? 'Hora exacta · ' : 'Exact time · '}
          <span className="font-bold">{schedLabel}</span>{' '}
          <span className="opacity-60">(Colombia)</span>
        </p>
      )}

      <div className="mt-10 inline-flex items-center gap-3 rounded-full bg-white/10 border border-white/20 backdrop-blur px-5 py-3">
        <Users className="w-4 h-4 text-[#F4DD8C]" />
        <span className="text-sm">
          {lang === 'es' ? 'Inscritos en este momento' : 'Registered right now'}
        </span>
        <span className="font-display text-2xl font-black tracking-tighter text-[#F4DD8C]">
          {total}
        </span>
      </div>

      <div className="mt-10">
        <Link
          to="/registro"
          className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#FF7597] text-white text-sm font-bold tracking-widest uppercase hover:bg-[#FF5C83] hover:scale-105 transition-all shadow-[0_12px_40px_rgba(255,117,151,0.35)]"
        >
          <Gift className="w-4 h-4" />
          {lang === 'es' ? '¡Aún puedes inscribirte!' : 'You can still enter!'}
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
};

const CountdownCell = ({ value, label, pulse }) => (
  <div
    className={`rounded-3xl bg-white/10 border border-white/20 backdrop-blur p-4 md:p-6 ${
      pulse ? 'animate-pulse' : ''
    }`}
  >
    <p
      className="font-brand text-5xl md:text-7xl lg:text-8xl leading-none text-[#F4DD8C]"
      style={{ textShadow: '4px 4px 0 rgba(31,26,36,0.15)' }}
    >
      {String(value).padStart(2, '0')}
    </p>
    <p className="mt-2 text-[10px] md:text-xs font-bold tracking-[0.3em] uppercase text-white/70">
      {label}
    </p>
  </div>
);

const DrawingView = ({ lang }) => (
  <div className="mt-16 text-center">
    <p className="inline-flex items-center gap-2 text-[10px] md:text-xs font-bold tracking-[0.4em] uppercase text-[#1F1A24] bg-[#F4DD8C] px-4 py-2 rounded-full">
      <Sparkles className="w-3.5 h-3.5 animate-spin" />
      {lang === 'es' ? 'SORTEANDO…' : 'DRAWING…'}
    </p>
    <h2 className="mt-6 font-display text-5xl md:text-7xl font-black tracking-tighter text-white animate-pulse">
      {lang === 'es' ? 'Eligiendo ganadora…' : 'Picking winner…'}
    </h2>
  </div>
);

const WinnerReveal = ({ winners, lang }) => {
  const n = winners.length;
  const isMulti = n > 1;
  const headline = lang === 'es'
    ? (isMulti ? `¡TENEMOS ${n} GANADORAS!` : '¡TENEMOS GANADORA!')
    : (isMulti ? `WE HAVE ${n} WINNERS!` : 'WE HAVE A WINNER!');
  const cols = Math.min(n, 3);
  return (
  <div data-testid="raffle-winner" className="mt-10 md:mt-16 text-center">
    <p className="inline-flex items-center gap-2 text-[10px] md:text-xs font-bold tracking-[0.4em] uppercase text-[#1F1A24] bg-[#F4DD8C] px-4 py-2 rounded-full animate-bounce">
      <Trophy className="w-3.5 h-3.5" />
      {headline}
    </p>

    <div className="mt-10 grid gap-6 max-w-5xl mx-auto" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
      {winners.map((w, i) => (
        <div
          key={`winner-${i}`}
          className="rounded-[2.5rem] bg-gradient-to-br from-[#F4DD8C] via-[#FF7597] to-[#A99BE0] p-8 md:p-12 text-[#1F1A24] shadow-[0_24px_60px_rgba(244,221,140,0.4)] animate-fade-up"
          style={{ animationDelay: `${i * 200}ms` }}
        >
          <Trophy className="w-10 h-10 mb-4 mx-auto text-white drop-shadow-md" />
          <p className="font-brand text-6xl md:text-8xl leading-none capitalize"
             style={{ textShadow: '4px 4px 0 rgba(255,255,255,0.5)' }}>
            {w.first_name}
          </p>
          {w.city && (
            <p className="mt-4 text-sm md:text-base font-bold tracking-widest uppercase opacity-80">
              {w.city}
            </p>
          )}
        </div>
      ))}
    </div>

    <p className="mt-10 text-white text-base md:text-xl">
      {lang === 'es'
        ? (isMulti
            ? '¡Felicidades a todas! Acérquense al stand de Jelly para reclamar su kit 💕'
            : '¡Felicidades! Acércate al stand de Jelly para reclamar tu kit 💕')
        : (isMulti
            ? 'Congrats to all! Come to the Jelly booth to claim your kits 💕'
            : 'Congrats! Come to the Jelly booth to claim your kit 💕')}
    </p>
  </div>
  );
};

const AnimatedNumber = ({ value }) => {
  const [display, setDisplay] = useState(value);
  useEffect(() => {
    let raf;
    const start = display;
    const end = value;
    if (start === end) return;
    const duration = 800;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      const v = Math.floor(start + (end - start) * easeOutCubic(p));
      setDisplay(v);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <span>{display}</span>;
};
const easeOutCubic = (x) => 1 - Math.pow(1 - x, 3);

const StatPill = ({ icon, label, value, accent }) => (
  <div className={`rounded-3xl p-5 backdrop-blur border ${accent ? 'bg-[#F4DD8C] text-[#1F1A24] border-transparent' : 'bg-white/10 text-white border-white/15'}`}>
    <div className="flex items-center gap-2 mb-2 opacity-80">{icon}<span className="text-[10px] tracking-widest uppercase font-bold">{label}</span></div>
    <p className="font-display text-4xl font-black tracking-tighter">{value}</p>
  </div>
);
